import {StationLayout, StationParts} from '@domain/kiln-monitoring'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import FlipIcon from '@mui/icons-material/Flip'
import {Button, FormControlLabel, Stack, Switch, Typography} from '@mui/material'
import {useSnackbar} from 'notistack'
import {Dispatch, SetStateAction} from 'react'
import {useTranslation} from 'react-i18next'

import {getConnectedSignals, useConnectedSignals} from '../../../hooks'
import {useKilnConfig} from '../../../providers'
import {KilnConfigActionType, SignalsModalData} from '../../../types'
import {KilnConfigTextField, KilnPartDimensionsInputs} from '../common'
import {ConnectSignalsButton} from '../ConnectSignalsButton'

interface KilnStationConfigInputsProps {
  selectedStationId: string
  disabledSignals: string[]
  setSignalsModalData: Dispatch<SetStateAction<SignalsModalData | undefined>>
  setSelectedKilnPartId: Dispatch<SetStateAction<string>>
}

export const KilnStationConfigInputs: React.FC<KilnStationConfigInputsProps> = ({
  selectedStationId,
  disabledSignals,
  setSignalsModalData,
  setSelectedKilnPartId
}) => {
  const {t} = useTranslation()
  const {enqueueSnackbar} = useSnackbar()

  const {kilnLayout, dispatch} = useKilnConfig()

  const selectedStation = kilnLayout.stations.find(
    (station) => station.id === selectedStationId
  ) as StationLayout

  const connectedSignals = useConnectedSignals({
    selectedSignals: kilnLayout.selectedSignals,
    kilnPartId: selectedStation.id
  })

  // toast notification about removing relevant signals
  const handleToggle = (addThrustRoller: boolean) => {
    if (!addThrustRoller) {
      const connectedSignals = getConnectedSignals(
        kilnLayout.selectedSignals,
        `${selectedStation.id}_${StationParts.THRUST_ROLLER}`
      )
      dispatch({type: KilnConfigActionType.REMOVE_THRUST_ROLLER, payload: {id: selectedStation.id}})
      if (connectedSignals.length === 0) return

      enqueueSnackbar(
        `Thrust roller removed. Following signals were disconnected: ${connectedSignals.join(
          ', '
        )}`,
        {
          variant: 'warning'
        }
      )
      return
    }

    dispatch({
      type: KilnConfigActionType.ADD_THRUST_ROLLER,
      payload: {
        id: selectedStation.id,
        position: kilnLayout.materialFlow === 'leftToRight' ? 'right' : 'left'
      }
    })
  }

  return (
    <Stack spacing={2}>
      <Typography variant="h3">{t('kilnConfig.stationDetails')}</Typography>
      <Stack direction="row" spacing={1.5}>
        <KilnConfigTextField
          label={t('kilnConfig.name')}
          value={selectedStation.name ?? ''}
          data-test-id="station-name-input"
          onChange={(e) =>
            dispatch({
              type: KilnConfigActionType.SET_STATION_NAME,
              payload: {id: selectedStation.id, name: e.target.value}
            })
          }
        />
        <ConnectSignalsButton
          connectedSignals={connectedSignals.length}
          onClick={() =>
            setSignalsModalData({
              type: 'station',
              layout: selectedStation,
              disabledSignals: disabledSignals
            })
          }
          testId="station-connect-signal"
        />

        <Button
          variant="outlined"
          color="error"
          onClick={() => {
            setSelectedKilnPartId(
              kilnLayout.stations
                .sort((stationA, stationB) => stationA.x - stationB.x)
                .find((station) => station.id !== selectedStation.id)?.id ?? kilnLayout.drive.id
            )
            dispatch({
              type: KilnConfigActionType.REMOVE_STATION,
              payload: {id: selectedStation.id}
            })
          }}
          startIcon={<DeleteOutlineIcon />}
          data-test-id="station-delete-btn"
        >
          {t('kilnConfig.delete')}
        </Button>
      </Stack>
      <Stack direction="row" spacing={1.5}>
        <FormControlLabel
          control={
            <Switch
              checked={selectedStation.thrustRollerPosition !== 'none'}
              data-test-id="station-roller-on-switch"
              onClick={(e: any) => handleToggle(e.target.checked)}
            />
          }
          label={t('kilnConfig.thrustRoller')}
        />
        <Button
          variant="outlined"
          color="secondary"
          disabled={selectedStation.thrustRollerPosition === 'none'}
          startIcon={<FlipIcon />}
          sx={{border: '1px solid #E8ECF0'}}
          onClick={() =>
            dispatch({
              type: KilnConfigActionType.FLIP_THRUST_ROLLER,
              payload: {id: selectedStation.id}
            })
          }
          data-test-id="station-roller-flip-btn"
        >
          {t('kilnConfig.flipRoller')}
        </Button>
      </Stack>
      <KilnPartDimensionsInputs selectedKilnPart={selectedStation} />
    </Stack>
  )
}
